import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { Container, Typography, Box, IconButton, Tooltip, Alert, useMediaQuery, useTheme } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { TrussProvider, parseWindEntry, useTruss } from '../../context/TrussContext';
import TrussVisualization from './TrussVisualization';
import BuildingIsometricView from './BuildingIsometricView';
import TrussInputForm from './TrussInputForm';
import TrussResults from './TrussResults';
import CalculationHistory from '../shared/CalculationHistory';
import { openTrussPrintReport } from './openTrussPrintReport';

function TrussCalculatorInner() {
  const historyRef = useRef(null);
  const resultsSectionRef = useRef(null);
  const [resultsInView, setResultsInView] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    inputs,
    buildApiPayload,
    results,
    setResults,
    previewGeometry,
    setPreviewGeometry,
    previewBracingLayout,
    setPreviewBracingLayout,
    loading,
    setLoading,
    error,
    setError,
    resultsOutOfDate,
    setResultsOutOfDate,
    loadFromSaved,
  } = useTruss();

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`/api/truss-analysis/saved-analyses/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const d = r.data;
        loadFromSaved(d.input_data, d.results_data);
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, loadFromSaved]);

  useEffect(() => {
    const payload = buildApiPayload();
    const t = setTimeout(() => {
      axios
        .post('/api/truss-analysis/calculations/preview-geometry/', payload)
        .then((r) => {
          setPreviewGeometry(r.data.geometry);
          setPreviewBracingLayout(r.data.bracing_layout ?? null);
        })
        .catch(() => {
          setPreviewGeometry(null);
          setPreviewBracingLayout(null);
        });
    }, 450);
    return () => clearTimeout(t);
  }, [buildApiPayload, setPreviewGeometry, setPreviewBracingLayout]);

  useEffect(() => {
    const node = resultsSectionRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setResultsInView(entry.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [results]);

  const handleCalculate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/truss-analysis/calculations/analyze/', buildApiPayload());
      const data = res.data.results;
      if (data.error) {
        setError(data.error);
        setResults(data);
      } else {
        setResults(data);
        setError(null);
        setResultsOutOfDate(false);
        // Keep preview mesh aligned with analysis (auto-design picks n_panels; preview alone uses optimal default).
        if (data.geometry) setPreviewGeometry(data.geometry);
      }
      historyRef.current?.saveAuto?.({
        input_data: buildApiPayload(),
        results_data: data.error ? null : data,
        chord_family: inputs.chord_family,
        summary_span_m: Number(inputs.span_m),
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Analysis failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [buildApiPayload, setResults, setLoading, setError, setResultsOutOfDate, inputs.chord_family, inputs.span_m]);

  const getSavePayload = useCallback(
    (isAuto) => ({
      input_data: buildApiPayload(),
      results_data: results && !results.error ? results : null,
      chord_family: inputs.chord_family,
      summary_span_m: Number(inputs.span_m),
    }),
    [buildApiPayload, results, inputs.chord_family, inputs.span_m],
  );

  /* When results are current, use analysis geometry (auto-design n_panels vs preview optimal default caused mismatched d9 etc.). */
  const geom =
    results && !results.error && !resultsOutOfDate && results.geometry
      ? results.geometry
      : previewGeometry ?? null;

  const characteristicLineLoads = useMemo(() => {
    const building = !!inputs.building_mode;
    const spacing = Math.max(Number(inputs.frame_spacing_m) || 6, 0.1);
    const g = building ? Number(inputs.g_kN_m2 || 0) * spacing : Number(inputs.g_kN_m || 0);
    const snow = building ? 0 : Number(inputs.snow_kN_m || 0);
    const imposed = building ? Number(inputs.imposed_kN_m2 || 0) * spacing : Number(inputs.imposed_kN_m || 0);
    const wind = parseWindEntry(inputs.wind_kN_m);
    return {
      w_top_kN_m: g + snow + imposed + (wind.kind === 'line' ? wind.value : 0),
      w_bottom_kN_m: Number(inputs.bottom_chord_kN_m || 0),
    };
  }, [
    inputs.building_mode,
    inputs.frame_spacing_m,
    inputs.g_kN_m,
    inputs.g_kN_m2,
    inputs.snow_kN_m,
    inputs.imposed_kN_m,
    inputs.imposed_kN_m2,
    inputs.wind_kN_m,
    inputs.bottom_chord_kN_m,
  ]);

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <Typography variant="h4" component="h1">
            Truss &amp; frame (steel)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Print report">
              <span>
                <IconButton
                  color="inherit"
                  disabled={!results || results.error}
                  onClick={() =>
                    openTrussPrintReport({
                      geometry: geom,
                      results,
                      inputs: buildApiPayload(),
                      resultsOutOfDate,
                    })
                  }
                >
                  <PrintIcon />
                </IconButton>
              </span>
            </Tooltip>
            <CalculationHistory
              ref={historyRef}
              apiEndpoint="/api/truss-analysis/saved-analyses"
              getSavePayload={getSavePayload}
              appLabel="Truss calculator"
              onLoad={({ input_data, results_data }) => loadFromSaved(input_data, results_data)}
              renderSummary={(item) =>
                [item.summary_span_m != null ? `${item.summary_span_m} m` : null, item.chord_family]
                  .filter(Boolean)
                  .join(' · ')
              }
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box>
          <Box
            sx={{
              position: resultsInView ? 'static' : { xs: 'static', sm: 'sticky' },
              top: 8,
              zIndex: 2,
              mb: 2,
              bgcolor: 'background.default',
              pb: 1,
            }}
          >
            {inputs.building_mode ? (
              <BuildingIsometricView
                inputs={inputs}
                geometry={geom}
                bracingLayout={results?.bracing_layout ?? previewBracingLayout}
                title="Building layout & bracing indices"
                collapsible
                defaultExpanded={!isMobile}
              />
            ) : null}
            <TrussVisualization
              geometry={geom}
              lineLoads={characteristicLineLoads}
              title={inputs.building_mode ? 'Governing frame (2D)' : 'Structure & loads'}
              collapsible={!!inputs.building_mode}
              defaultExpanded={!isMobile}
            />
          </Box>
          <TrussInputForm
            onSubmit={handleCalculate}
            loading={loading}
            lastComputeMs={results && typeof results.compute_time_ms === 'number' ? results.compute_time_ms : null}
          />
        </Box>
        <Box ref={resultsSectionRef}>
          <TrussResults results={results} resultsOutOfDate={resultsOutOfDate} geometry={geom} inputs={buildApiPayload()} />
        </Box>
      </Box>
    </Container>
  );
}

export default function TrussCalculator() {
  return (
    <TrussProvider>
      <TrussCalculatorInner />
    </TrussProvider>
  );
}
